"use client";

import { useMemo, useRef, useState } from "react";
import { DocumentPreview } from "@/components/tool-kit/document-preview";
import {
  ErrorNotice,
  Panel,
  StatCard,
  StatGrid,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY, WIDGET_H2 } from "@/components/tool-kit/styles";
import { CanvasDiagram } from "@/components/tools/business-templates/canvas-diagram";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  BLOCKS,
  buildCanvasDocument,
  CANVAS_WIDTH,
  type CanvasBlock,
  type CanvasInput,
  emptyBlocks,
  layoutBlock,
} from "@/lib/tool-kit/business-templates/canvas";
import { chartFilename } from "@/lib/tool-kit/charts/layout";
import { wrapText } from "@/lib/tool-kit/charts/text";
import { downloadPng, downloadSvg } from "@/lib/tool-kit/download";
import { splitItems } from "@/lib/tool-kit/essay-templates/text";

/** English strings. A translated content file overrides these through `ui`. */
export const BUSINESS_MODEL_CANVAS_GENERATOR_STRINGS = {
  tryExample: "Try an example",
  clear: "Clear the canvas",
  headerHeading: "About this canvas",
  nameLabel: "Business or project",
  namePlaceholder: "e.g. A campus bike-repair stand",
  designedForLabel: "Designed for",
  designedByLabel: "Designed by",
  dateLabel: "Date",
  versionLabel: "Version",
  blocksHeading: "The nine blocks — one item per line",
  blockNames: {
    segments: "Customer Segments",
    value: "Value Propositions",
    channels: "Channels",
    relationships: "Customer Relationships",
    revenue: "Revenue Streams",
    resources: "Key Resources",
    activities: "Key Activities",
    partners: "Key Partners",
    costs: "Cost Structure",
  },
  questions: {
    segments:
      "For whom are you creating value? Who are your most important customers, and how do they differ from each other?",
    value:
      "What value do you deliver? Which customer problem do you solve, and which needs do you satisfy, for each segment?",
    channels:
      "Through which channels do your customers want to be reached, how do you reach them now, and which work best?",
    relationships:
      "What kind of relationship does each segment expect — personal help, self-service, a community — and what does it cost?",
    revenue:
      "What are customers really willing to pay for, how do they pay now, and how much does each stream contribute?",
    resources:
      "What assets — physical, intellectual, human, financial — do your value propositions, channels and revenue streams require?",
    activities:
      "What are the most important things the business must do to make the model work?",
    partners:
      "Who are your key partners and suppliers, which resources do they supply, and which activities do they perform?",
    costs:
      "What are the biggest costs in the model? Which resources and activities are the most expensive?",
  },
  placeholders: {
    segments: "e.g. Students who cycle to campus",
    value: "e.g. Fast, cheap repairs between classes",
    channels: "e.g. Walk-up stand; booking on Instagram",
    relationships: "e.g. Friendly, personal service; loyalty card",
    revenue: "e.g. Repair fees; a monthly service plan",
    resources: "e.g. Tools and a repair stand; a trained mechanic",
    activities: "e.g. Repairing bikes; ordering spare parts",
    partners: "e.g. Parts wholesaler; the student union",
    costs: "e.g. Spare parts; stand permit; mechanic's pay",
  },
  resultsHeading: "Your canvas",
  filledStat: "Blocks filled",
  filledValue: "{filled} of {total}",
  itemsStat: "one:Item entered|other:Items entered",
  moreText: "+{count} more — see the text version",
  canvasTitle: "Business Model Canvas",
  canvasTitleWithName: "Business Model Canvas: {name}",
  detailSeparator: "   ·   ",
  headerDesignedFor: "Designed for: {value}",
  headerDesignedBy: "Designed by: {value}",
  headerDate: "Date: {value}",
  headerVersion: "Version: {value}",
  footer:
    "The Business Model Canvas. Designed by: Strategyzer AG, the makers of Business Model Generation and Strategyzer — strategyzer.com. This work is licensed under the Creative Commons Attribution-Share Alike 3.0 Unported License: creativecommons.org/licenses/by-sa/3.0/. This filled-in canvas is an adaptation of the original and is shared under the same licence.",
  canvasLabel: "Business Model Canvas",
  canvasDescription:
    "The nine-block Business Model Canvas: key partners, key activities, key resources, value propositions, customer relationships, channels, customer segments, cost structure and revenue streams, filled in with your entries.",
  downloadSvg: "Download canvas (SVG)",
  downloadPng: "Download canvas (PNG)",
  print: "Print canvas",
  exportFailed: "The PNG could not be created. Download the SVG instead.",
  fileName: "business-model-canvas",
  document: {
    heading: "Text version",
    prompt: "To write",
    guidance: "Tip",
    copy: "Copy",
    copied: "Copied!",
    downloadText: "Download .txt",
    downloadMarkdown: "Download .md",
    print: "Print",
    remaining: "one: block still to fill|other: blocks still to fill",
    complete: "Every block has your content.",
  },
  defaultTitle: "Business Model Canvas",
  titleWithName: "Business Model Canvas: {name}",
  subtitle:
    "The Business Model Canvas by Alexander Osterwalder and Yves Pigneur (Business Model Generation, 2010)",
  prompts: {
    segments: "List the customer groups you serve, most important first.",
    value: "State what you offer each segment and the problem it solves.",
    channels: "List how each segment finds, buys and receives your offer.",
    relationships: "Say what relationship each segment expects.",
    revenue: "List each revenue stream and how customers pay.",
    resources: "List the assets the model needs.",
    activities: "List the activities the model depends on.",
    partners: "List the partners and suppliers you rely on.",
    costs: "List the main costs, largest first.",
  },
  fields: {
    designedFor: "Designed for",
    designedBy: "Designed by",
    date: "Date",
    version: "Version",
  },
  checksHeading: "Check the Model",
  checksNote:
    "A canvas works as a whole: each block should connect to the others. These checks are prompts, not scores.",
  checkEmpty: "Still empty: {names}.",
  checkFilled:
    "All nine blocks have content. Now read across: does every segment have a value proposition, a channel, a relationship and a revenue stream?",
  checkSegments:
    "With more than one customer segment, repeat the check for each: a value proposition, channel, relationship and revenue stream that suit that segment, not just the first one.",
  listSeparator: ", ",
  attributionHeading: "Licence and Credit",
  attribution:
    "The Business Model Canvas. Designed by: Strategyzer AG, the makers of Business Model Generation and Strategyzer (strategyzer.com). This work is licensed under the Creative Commons Attribution-Share Alike 3.0 Unported License (creativecommons.org/licenses/by-sa/3.0/). This filled-in canvas is an adaptation and is shared under the same licence.",
  example: {
    name: "A campus bike-repair stand",
    designedFor: "Entrepreneurship 101",
    designedBy: "Group 4",
    date: "2026-10-16",
    version: "1",
    segments:
      "Students who cycle to campus\nStaff who cycle to work\nStudent societies running bike events",
    value:
      "Repairs done between classes, usually in under 30 minutes\nPrices below the nearest bike shop\nFree safety check with every repair",
    channels:
      "Walk-up stand outside the library\nBooking by Instagram message\nPosters on residence notice boards",
    relationships:
      "Friendly, personal service\nLoyalty card: every sixth repair half price\nReminders before winter",
    revenue:
      "Repair fees\nA termly service plan\nCommission on lights and locks",
    resources:
      "Tools and a repair stand\nA trained part-time mechanic\nPermission to trade on campus",
    activities:
      "Repairing and servicing bikes\nOrdering spare parts\nPromoting the stand on social media",
    partners:
      "Local parts wholesaler\nThe student union (space and permit)\nA bike-safety charity",
    costs:
      "Spare parts\nMechanic's pay\nStand permit and insurance\nTool replacement",
  },
} as const;

const EMPTY: CanvasInput = {
  name: "",
  designedFor: "",
  designedBy: "",
  date: "",
  version: "",
  segments: "",
  value: "",
  channels: "",
  relationships: "",
  revenue: "",
  resources: "",
  activities: "",
  partners: "",
  costs: "",
};

const FOOTER_WIDTH = CANVAS_WIDTH - 40;

type TextKey = keyof CanvasInput;

export default function BusinessModelCanvasGeneratorWidget() {
  const strings = useToolUi(BUSINESS_MODEL_CANVAS_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [input, setInput] = useState<CanvasInput>(EMPTY);
  const [exportFailed, setExportFailed] = useState(false);
  const holder = useRef<HTMLDivElement>(null);
  const update = (key: TextKey) => (value: string) =>
    setInput((previous) => ({ ...previous, [key]: value }));

  const doc = useMemo(
    () => buildCanvasDocument(input, strings),
    [input, strings]
  );
  const layouts = BLOCKS.map((block) =>
    layoutBlock(block, splitItems(input[block]), (count) =>
      t(strings.moreText, { count: fmt(count) })
    )
  );
  const filled = BLOCKS.length - emptyBlocks(input).length;
  const itemCount = BLOCKS.reduce(
    (sum, block) => sum + splitItems(input[block]).length,
    0
  );
  const name = input.name.trim();
  const title = name
    ? t(strings.canvasTitleWithName, { name })
    : strings.canvasTitle;
  const details = [
    [strings.headerDesignedFor, input.designedFor],
    [strings.headerDesignedBy, input.designedBy],
    [strings.headerDate, input.date],
    [strings.headerVersion, input.version],
  ]
    .filter(([, value]) => value.trim() !== "")
    .map(([template, value]) => t(template, { value: value.trim() }))
    .join(strings.detailSeparator);
  const footer = wrapText(strings.footer, FOOTER_WIDTH, { fontSize: 11 }, 3);
  const baseName = chartFilename(name, strings.fileName);

  function svg() {
    return holder.current?.querySelector("svg") ?? null;
  }

  async function savePng() {
    const element = svg();
    if (!element) {
      return;
    }
    try {
      setExportFailed(false);
      await downloadPng(`${baseName}.png`, element, { scale: 2 });
    } catch {
      setExportFailed(true);
    }
  }

  return (
    <div className="space-y-8">
      <style>
        {"@media print { @page { size: landscape; margin: 10mm; } }"}
      </style>
      <div className="space-y-6 print:hidden">
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_SECONDARY}
            onClick={() => setInput({ ...EMPTY, ...strings.example })}
            type="button"
          >
            {strings.tryExample}
          </button>
          <button
            className={BUTTON_SECONDARY}
            onClick={() => setInput(EMPTY)}
            type="button"
          >
            {strings.clear}
          </button>
        </div>
        <Panel title={strings.headerHeading}>
          <TextField
            id="bmc-name"
            label={strings.nameLabel}
            onChange={update("name")}
            placeholder={strings.namePlaceholder}
            value={input.name}
          />
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <TextField
              id="bmc-for"
              label={strings.designedForLabel}
              onChange={update("designedFor")}
              value={input.designedFor}
            />
            <TextField
              id="bmc-by"
              label={strings.designedByLabel}
              onChange={update("designedBy")}
              value={input.designedBy}
            />
            <TextField
              id="bmc-date"
              label={strings.dateLabel}
              onChange={update("date")}
              value={input.date}
            />
            <TextField
              id="bmc-version"
              label={strings.versionLabel}
              onChange={update("version")}
              value={input.version}
            />
          </div>
        </Panel>
        <Panel title={strings.blocksHeading}>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {BLOCKS.map((block: CanvasBlock) => (
              <TextAreaField
                hint={strings.questions[block]}
                id={`bmc-${block}`}
                key={block}
                label={strings.blockNames[block]}
                onChange={update(block)}
                placeholder={strings.placeholders[block]}
                prose
                rows={4}
                value={input[block]}
              />
            ))}
          </div>
        </Panel>
      </div>

      <section aria-live="polite" className="space-y-3 print:hidden">
        <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
        <StatGrid>
          <StatCard
            label={strings.filledStat}
            value={t(strings.filledValue, {
              filled: fmt(filled),
              total: fmt(BLOCKS.length),
            })}
          />
          <StatCard
            label={tPlural(strings.itemsStat, locale, itemCount)}
            value={fmt(itemCount)}
          />
        </StatGrid>
      </section>

      <figure className="space-y-3">
        <div
          className="overflow-hidden rounded-lg border border-border bg-white"
          ref={holder}
        >
          <CanvasDiagram
            description={strings.canvasDescription}
            footer={footer}
            header={{ title, details: details ? [details] : [] }}
            id="bmc-canvas"
            label={strings.canvasLabel}
            layouts={layouts}
            names={strings.blockNames}
          />
        </div>
        <div className="flex flex-wrap gap-3 print:hidden">
          <button
            className={BUTTON_SECONDARY}
            onClick={() => {
              const element = svg();
              if (element) {
                downloadSvg(`${baseName}.svg`, element);
              }
            }}
            type="button"
          >
            {strings.downloadSvg}
          </button>
          <button className={BUTTON_SECONDARY} onClick={savePng} type="button">
            {strings.downloadPng}
          </button>
          <button
            className={BUTTON_SECONDARY}
            onClick={() => window.print()}
            type="button"
          >
            {strings.print}
          </button>
        </div>
        {exportFailed ? (
          <ErrorNotice>{strings.exportFailed}</ErrorNotice>
        ) : null}
      </figure>

      <div className="print:hidden">
        <DocumentPreview
          doc={doc}
          fileName={strings.fileName}
          idPrefix="bmc"
          labels={strings.document}
        />
      </div>
    </div>
  );
}
